import { useState, useEffect } from 'react';
import * as React from 'react';

import {
  Typography,
  Paper,
  LinearProgress,
  CircularProgress,
  Box,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { toMMSS } from '../../../utils';
import type { ITheme } from '../../../theme';
import TranscriptionProgressTip from './TranscriptionProgressTip';

const useStyles = makeStyles()((theme: ITheme) => ({
  card: {
    padding: '1.25rem',
    marginBottom: '1rem',
    width: '80%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  cardTitle: {
    marginTop: 0,
    fontSize: '2em',
    overflowWrap: 'anywhere',
    lineHeight: '1.4em',
    marginBottom: 20,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.3em',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '2em',
    },
  },
  message: {
    width: '100%',
    height: 25,
    marginTop: 5,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7em',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1em',
    },
  },
  textLeft: { float: 'left' },
  textRight: { float: 'right' },
  box1: { position: 'relative', display: 'inline-flex' },
  box2: {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export interface TranscriptionProgressProps {
  title: string;
  message: string;
  progress: number;
  estimatedText: string;
  processingTime: number;
  estimatedDuration: number;
}

const TranscriptionProgress: React.FC<TranscriptionProgressProps> = ({
  title,
  message,
  progress,
  estimatedText,
  processingTime,
  estimatedDuration,
}) => {
  const { classes } = useStyles();
  const isMobile = useMediaQuery((theme: ITheme) => theme.breakpoints.down('xs'));

  const [progressUpdate, setProgressUpdate] = useState(0);
  const [reset, setReset] = useState(false);
  let timer = 0;
  let helperUpdate = 0;

  useEffect(() => {
    setInterval(() => {
      timer += 1;
      if (reset) {
        timer = 0;
        setReset(false);
      }
      if (timer >= 5) {
        if (helperUpdate < 45) {
          helperUpdate += 1;
          setProgressUpdate((oldProgressUpdate) => oldProgressUpdate + 1);
        }
      }
    }, 1000);
  }, []);

  useEffect(() => {
    setReset(true);
  }, [progress]);

  return (
    <Paper className={classes.card} elevation={0}>
      <Box display="flex" alignItems="center" justifyContent="center">
        <Typography className={classes.cardTitle} variant="h3" component="h3">
          {title}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="center">
        <Box className={classes.box1}>
          <CircularProgress size={isMobile ? 50 : 100} />
          <Box className={classes.box2}>
            <Typography component="div" variant={isMobile ? 'subtitle1' : 'h5'}>
              {`${progress > progressUpdate ? progress : progressUpdate}%`}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="center" height={80}>
        <TranscriptionProgressTip isMobile={isMobile} />
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress > progressUpdate ? progress : progressUpdate}
      />
      <div className={classes.message}>
        <span className={classes.textLeft}>
          {message}
          {' '}
          (
          {toMMSS(processingTime)}
          )
        </span>
        <span className={classes.textRight}>
          (
          {estimatedText}
          :
          {' '}
          {toMMSS(estimatedDuration)}
          )
        </span>
      </div>
    </Paper>
  );
};

export default TranscriptionProgress;
